import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { adminStorage } from "../../features/admin/service";
import { apiClient } from "../../lib/http/apiClient";
import { parseApiError } from "../../lib/http/apiErrors";
import ConfirmModal from "../../components/admin/ConfirmModal";
import Spinner from "../../components/ui/Spinner";
import { useAdminToast } from "../../context/AdminToastContext";

const ROUTE_OPTIONS = [
  "deals", "ai_itinerary", "flyers", "videos",
  "market_research", "my_assets", "ai_slide",
];

interface Chip {
  id: string;
  name: string;
  category: string;
  route_mapping: string | null;
  priority: number;
  active: boolean;
  trending: boolean;
  created_by?: string;
  created_at?: string;
}

interface WelcomeSetting {
  id: string;
  role: "buyer" | "seller";
  welcome_sub: string;
  updated_at?: string | null;
}

const emptyForm = () => ({
  name: "",
  category: "general",
  route_mapping: "",
  priority: 0,
  active: true,
  trending: false,
});

function adminHeaders() {
  const token = adminStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function AdminChipsPage() {
  const [chips, setChips] = useState<Chip[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { showError, showSuccess } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);
  useEffect(() => { if (success) showSuccess(success); }, [success, showSuccess]);
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Chip | null>(null);
  const [viewChip, setViewChip] = useState<Chip | null>(null);

  // ── Welcome message settings ──────────────────────────────────────────────
  const [welcomeSettings, setWelcomeSettings] = useState<WelcomeSetting[]>([]);
  const [welcomeLoading, setWelcomeLoading] = useState(true);
  const [welcomeSaving, setWelcomeSaving] = useState<string | null>(null);
  const [welcomeDrafts, setWelcomeDrafts] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/rag/chips/admin", { headers: adminHeaders() });
      setChips(res.data);
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadWelcomeSettings = useCallback(async () => {
    setWelcomeLoading(true);
    try {
      const res = await apiClient.get<WelcomeSetting[]>("/rag/welcome-settings", { headers: adminHeaders() });
      setWelcomeSettings(res.data);
      const drafts: Record<string, string> = {};
      for (const s of res.data) {
        drafts[s.role] = s.welcome_sub;
      }
      setWelcomeDrafts(drafts);
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setWelcomeLoading(false);
    }
  }, []);

  useEffect(() => { void load(); void loadWelcomeSettings(); }, [load, loadWelcomeSettings]);

  const findWelcomeSetting = (role: "buyer" | "seller") =>
    welcomeSettings.find((s) => s.role === role);

  const handleSaveWelcomeSub = async (role: "buyer" | "seller") => {
    const value = (welcomeDrafts[role] ?? "").trim();
    if (!value) { setError("Welcome sub-text cannot be empty."); return; }
    setWelcomeSaving(role); setError(null);
    try {
      await apiClient.put("/rag/welcome-settings", { role, welcome_sub: value }, { headers: adminHeaders() });
      setSuccess(`Updated welcome message for ${role}.`);
      await loadWelcomeSettings();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setWelcomeSaving(null);
    }
  };

  const handleClearWelcomeSub = async (role: "buyer" | "seller") => {
    const existing = findWelcomeSetting(role);
    if (!existing) return;
    setWelcomeSaving(role); setError(null);
    try {
      await apiClient.delete(`/rag/welcome-settings/${existing.id}`, { headers: adminHeaders() });
      setSuccess(`Reverted welcome message for ${role} to default.`);
      await loadWelcomeSettings();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setWelcomeSaving(null);
    }
  };

  const handleEdit = (chip: Chip) => {
    setEditingId(chip.id);
    setForm({
      name: chip.name,
      category: chip.category,
      route_mapping: chip.route_mapping ?? "",
      priority: chip.priority,
      active: chip.active,
      trending: chip.trending,
    });
    setError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(deleteTarget.id);
    try {
      await apiClient.delete(`/rag/chips/${deleteTarget.id}`, { headers: adminHeaders() });
      setDeleteTarget(null);
      setSuccess("Chip deleted.");
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  const handleToggle = async (chip: Chip, field: "active" | "trending") => {
    setSaving(chip.id + field);
    try {
      await apiClient.put(`/rag/chips/${chip.id}`, { [field]: !chip[field] }, { headers: adminHeaders() });
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Chip name is required."); return; }
    setSaving("form"); setError(null);
    try {
      const body = { ...form, route_mapping: form.route_mapping || null, priority: Number(form.priority) };
      if (editingId) {
        await apiClient.put(`/rag/chips/${editingId}`, body, { headers: adminHeaders() });
        setSuccess(`Updated "${form.name}".`);
      } else {
        await apiClient.post("/rag/chips", body, { headers: adminHeaders() });
        setSuccess(`Created "${form.name}".`);
      }
      setForm(emptyForm());
      setEditingId(null);
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  const filtered = chips.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.category ?? "").toLowerCase().includes(search.toLowerCase()) ||
    (c.route_mapping ?? "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
    <div>
      <AdminPageHeader
        title="Suggestion Chips"
        subtitle="Manage AI suggestion chips shown to users in the chat interface"
        actions={
          <button onClick={() => void load()} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-xl text-sm text-gray-300 transition-colors">
            Refresh
          </button>
        }
      />

      {error && <div className="mb-4 px-4 py-3 bg-red-900/40 border border-red-700 rounded-xl text-red-300 text-sm">{error}</div>}
      {success && <div className="mb-4 px-4 py-3 bg-green-900/40 border border-green-700 rounded-xl text-green-300 text-sm">{success}</div>}

      {/* Welcome message settings */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 mb-6">
        <h2 className="text-sm font-semibold text-white mb-1">Welcome Message</h2>
        <p className="text-gray-500 text-xs mb-4">
          Set the welcome sub-text shown under the greeting on the Chat Deal page,
          per role. The greeting itself rotates automatically.
        </p>
        {welcomeLoading ? (
          <div className="text-center text-gray-500 text-sm py-4"><Spinner /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(["buyer", "seller"] as const).map((role) => (
              <div key={role} className="space-y-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {role === "buyer" ? "Buyers" : "Sellers"}
                </h3>

                <div>
                  <label className="block text-xs text-gray-400 mb-1">Welcome message</label>
                  <textarea
                    rows={3}
                    value={welcomeDrafts[role] ?? ""}
                    onChange={(e) => setWelcomeDrafts((d) => ({ ...d, [role]: e.target.value }))}
                    placeholder="Default welcome sub-text for this role…"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                  />
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => void handleSaveWelcomeSub(role)} disabled={welcomeSaving === role}
                      className="px-3 py-1 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-xs font-medium transition-colors disabled:opacity-50">
                      {welcomeSaving === role ? "Saving…" : "Save"}
                    </button>
                    {findWelcomeSetting(role) && (
                      <button onClick={() => void handleClearWelcomeSub(role)} disabled={welcomeSaving === role}
                        className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-lg text-xs transition-colors disabled:opacity-50">
                        Reset
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create / Edit form */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 mb-6">
        <h2 className="text-sm font-semibold text-white mb-4">{editingId ? "Edit Chip" : "Add New Chip"}</h2>
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Label *</label>
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Show trending deals"
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Category</label>
              <input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                placeholder="e.g. deals, general"
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Route</label>
              <select value={form.route_mapping} onChange={(e) => setForm((f) => ({ ...f, route_mapping: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50">
                <option value="">No route (global)</option>
                {ROUTE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Priority</label>
              <input type="number" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: Number(e.target.value) }))}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50" />
            </div>
            <div className="flex items-end gap-6 pb-2">
              <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                  className="w-4 h-4 rounded accent-indigo-500" />
                Active
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                <input type="checkbox" checked={form.trending} onChange={(e) => setForm((f) => ({ ...f, trending: e.target.checked }))}
                  className="w-4 h-4 rounded accent-amber-500" />
                Trending
              </label>
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={saving === "form"}
              className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium transition-colors disabled:opacity-50">
              {saving === "form" ? "Saving…" : editingId ? "Update Chip" : "Add Chip"}
            </button>
            {editingId && (
              <button type="button" onClick={() => { setEditingId(null); setForm(emptyForm()); setError(null); }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-xl text-sm transition-colors">
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Chips table */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-800 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white">
            All Chips <span className="text-gray-500 font-normal ml-1">({filtered.length})</span>
          </h2>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…"
            className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-xs w-48 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 placeholder-gray-500" />
        </div>

        {loading ? (
          <div className="px-5 py-8 text-center text-gray-500 text-sm"><Spinner /></div>
        ) : filtered.length === 0 ? (
          <div className="px-5 py-8 text-center text-gray-500 text-sm">No chips found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-800">
                <tr>
                  <th className="text-left px-5 py-3 text-gray-500 font-medium text-xs">Label</th>
                  <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Category</th>
                  <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Route</th>
                  <th className="text-center px-4 py-3 text-gray-500 font-medium text-xs">Priority</th>
                  <th className="text-center px-4 py-3 text-gray-500 font-medium text-xs">Active</th>
                  <th className="text-center px-4 py-3 text-gray-500 font-medium text-xs">Trending</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {filtered.map((chip) => (
                  <tr key={chip.id} className="hover:bg-gray-800/20 transition-colors">
                    <td className="px-5 py-3 text-white text-sm font-medium">{chip.name}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{chip.category}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{chip.route_mapping ?? <span className="text-gray-600">global</span>}</td>
                    <td className="px-4 py-3 text-center text-gray-300 text-xs">{chip.priority}</td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => void handleToggle(chip, "active")} disabled={saving === chip.id + "active"}
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full transition-colors ${chip.active ? "bg-green-900/40 text-green-400" : "bg-gray-800 text-gray-500"}`}>
                        {chip.active ? "On" : "Off"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => void handleToggle(chip, "trending")} disabled={saving === chip.id + "trending"}
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full transition-colors ${chip.trending ? "bg-amber-900/40 text-amber-400" : "bg-gray-800 text-gray-500"}`}>
                        {chip.trending ? "🔥 Yes" : "No"}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => setViewChip(chip)}
                          className="px-3 py-1 bg-indigo-900/40 hover:bg-indigo-900/70 text-indigo-300 rounded-lg text-xs transition-colors">
                          View
                        </button>
                        <button onClick={() => setDeleteTarget(chip)} disabled={saving === chip.id}
                          className="px-3 py-1 bg-red-900/30 hover:bg-red-900/60 text-red-400 rounded-lg text-xs transition-colors disabled:opacity-50">
                          {saving === chip.id ? "…" : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>

    {viewChip && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setViewChip(null)}>
        <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
            <h2 className="text-lg font-semibold text-white truncate">{viewChip.name}</h2>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => { setViewChip(null); handleEdit(viewChip); }}
                className="px-3 py-1.5 text-xs font-medium text-indigo-400 bg-indigo-500/10 hover:bg-indigo-500/20 rounded-lg transition-colors"
              >
                Edit
              </button>
              <button onClick={() => setViewChip(null)} className="text-gray-400 hover:text-white text-xl leading-none">×</button>
            </div>
          </div>
          <div className="p-6 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <span className="text-gray-500">Category</span>
            <span className="text-gray-300">{viewChip.category}</span>
            <span className="text-gray-500">Route</span>
            <span className="text-gray-300">{viewChip.route_mapping ?? "global"}</span>
            <span className="text-gray-500">Priority</span>
            <span className="text-gray-300">{viewChip.priority}</span>
            <span className="text-gray-500">Active</span>
            <span className={viewChip.active ? "text-green-400" : "text-gray-500"}>{viewChip.active ? "Yes" : "No"}</span>
            <span className="text-gray-500">Trending</span>
            <span className={viewChip.trending ? "text-amber-400" : "text-gray-500"}>{viewChip.trending ? "Yes" : "No"}</span>
          </div>
        </div>
      </div>
    )}
    {deleteTarget && (
      <ConfirmModal
        title="Delete Chip"
        description={`"${deleteTarget.name}" will be permanently removed.`}
        items={["Remove chip from database", "Remove from all user sessions"]}
        confirmLabel="Delete Chip"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    )}
    </>
  );
}
